/**
 * M&M Builder Store — session-only writable state for the M&M module.
 *
 * Immutable workbook seed data (MM-001..MM-005, Cases, Reflections,
 * Imaging, Literature, People) is read through the repository. All
 * new M&M-side records — the demo session, its collaboration
 * records, transcript segments, generated synthesis, faculty
 * corrections and the published departmental-memory record — live
 * only in this store with a persistent DEMO-* id space so the two
 * layers never collide.
 *
 * Public shape mirrors what a production API would return so the
 * deterministic generator can later be swapped for a GPT structured
 * output call without touching the UI.
 */
import { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  getCaseById,
  getMMByCaseId,
  getReflectionsByCaseId,
  getOperativePearlsByCaseId,
  getImagingByCaseId,
  getLiteratureByIds,
  getPerson,
  getMMRecordById,
} from "@/data/repository";
import { synthesizeMM as synthesizeMMLive } from "@/data/llmClient";

const MMBuilderContext = createContext(null);
const nowIso = () => new Date().toISOString();

/* ---- id helpers ------------------------------------------------------- */
let __mmCounter = 0;
let __collabCounter = 0;
let __trxCounter = 0;
let __agendaCounter = 0;
let __pubCounter = 0;
let __teCounter = 0;
const mmId = () => `MM-DEMO-${String(++__mmCounter).padStart(3, "0")}`;
const collabId = () => `COLLAB-DEMO-${String(++__collabCounter).padStart(3, "0")}`;
const trxId = () => `TRX-DEMO-${String(++__trxCounter).padStart(3, "0")}`;
const agendaId = () => `AGENDA-DEMO-${String(++__agendaCounter).padStart(3, "0")}`;
const publishedId = () => `PUB-DEMO-${String(++__pubCounter).padStart(3, "0")}`;
const timelineEventId = () => `TLE-DEMO-${String(++__teCounter).padStart(3, "0")}`;

/* ---- deterministic transcript script for VS-RAO-001 ------------------- */
/* Every segment maps to a workbook Person ID so provenance stays real.  */
const VS_RAO_001_TRANSCRIPT_SCRIPT = [
  { speaker: "RES-001", role: "Senior resident", offsetSec: 8,   text: "Case VS-RAO-001 — 42-year-old, left Koos III vestibular schwannoma. Retrosigmoid approach. Intraoperative facial EMG showed a 60 percent amplitude drop with threshold rising from 0.08 to 0.6 milliamps during medial capsule dissection.", source: "chronology" },
  { speaker: "ATT-001", role: "Primary attending", offsetSec: 22, text: "The decrement occurred before proximal facial nerve identification was complete. Traction had been placed on a densely adherent capsule at the root exit zone with no defined plane.", source: "root-cause" },
  { speaker: "ATT-002", role: "Faculty",          offsetSec: 34, text: "This is a recognized failure mode. Applying traction before proximal identification is the single most avoidable factor in monitoring decrements during vestibular schwannoma resection.", source: "root-cause" },
  { speaker: "RES-002", role: "Junior resident",  offsetSec: 47, text: "What was the response protocol at that moment?", source: "practice-change" },
  { speaker: "ATT-001", role: "Primary attending", offsetSec: 55, text: "All traction released, dissection paused for ten minutes, and the nerve was remapped proximal to distal before resuming. That pause-and-remap step is what limited the injury to a House-Brackmann IV.", source: "practice-change" },
  { speaker: "ATT-003", role: "Faculty",          offsetSec: 68, text: "The intentional 0.4 cm residual over the densely adherent segment was the right call. Trading a residual for functional nerve preservation is the department's standing position on Koos III lesions with adherence.", source: "teaching" },
  { speaker: "RES-001", role: "Senior resident",  offsetSec: 82, text: "POD 1 imaging confirms the small planned residual. POD 1 exam: House-Brackmann IV, complete eye-care protocol initiated with steroid taper. Week 3: House-Brackmann II with complete eye closure. Six months: House-Brackmann I with a stable residual on surveillance MRI.", source: "chronology" },
  { speaker: "ATT-006", role: "Faculty",          offsetSec: 96, text: "That trajectory — HB IV at POD 1 improving to HB I by six months — is consistent with an anatomically intact nerve. The literature supports this: Sampath series and Samii cohort both show near-complete recovery when the nerve is anatomically preserved.", source: "literature" },
  { speaker: "ATT-001", role: "Primary attending", offsetSec: 108, text: "So the change in practice: proximal nerve identification is now an explicit documented step in the operative record before any capsule traction on adherent tumors.", source: "practice-change" },
  { speaker: "ATT-002", role: "Faculty",          offsetSec: 118, text: "And a departmental pause-and-remap protocol for any facial EMG decrement of 50 percent or more. Circulate the annotated imaging to the residency and add this case to the vestibular schwannoma pre-case review library.", source: "action" },
  { speaker: "RES-004", role: "Chief resident",   offsetSec: 130, text: "One outstanding question — does a 0.4 cm brainstem-adjacent residual warrant earlier surveillance imaging than our current 3-month cadence?", source: "unresolved" },
  { speaker: "ATT-001", role: "Primary attending", offsetSec: 140, text: "Good question. We will discuss surveillance cadence separately with the tumor board and update the pre-case review guidance if needed.", source: "unresolved" },
];

/* ---- deterministic synthesis generator -------------------------------
 * Composes final structured synthesis from:
 *   • workbook MM record for the case (chronology, decision points,
 *     factors, learning points already curated by the department);
 *   • linked reflections and operative pearls;
 *   • linked imaging IDs;
 *   • demo transcript segments;
 *   • demo collaboration records.
 * Every generated claim carries a minimal provenance bundle.
 */
function generateDeterministicSynthesis({
  caseId,
  transcriptSegments,
  collaborationRecords,
  demoPins,
  demoImagingComments,
}) {
  const caseRec = getCaseById(caseId);
  const workbookMM = (getMMByCaseId(caseId) || [])[0]; // MM-001 for VS-RAO-001
  const reflections = getReflectionsByCaseId(caseId);
  const pearls = getOperativePearlsByCaseId(caseId);
  const imaging = getImagingByCaseId(caseId);

  const trxByTopic = (topic) =>
    transcriptSegments.filter((s) => s.source === topic).map((s) => s.trxId);
  const collabIdsForTopic = (predicate) =>
    collaborationRecords.filter(predicate).map((c) => c.collaborationId);
  const imagingIds = imaging.slice(0, 2).map((i) => i.imagingId);
  const pinIds = demoPins.map((p) => p.pinId);
  const imagingCollabIds = demoImagingComments.map((c) => c.collaborationId);

  const litIds = workbookMM?.literatureIds || [];
  const reflIds = workbookMM?.reflectionIds || reflections.map((r) => r.reflectionId).slice(0, 3);
  const pearlIds = pearls.slice(0, 3).map((p) => p.operativePearlId);

  return {
    generatedAt: nowIso(),
    sourceMMId: workbookMM?.mAndMId || null, // prior workbook M&M cited
    classification: {
      outcomeCategory: "Morbidity",
      severity: "Major",
      preventability: "Potentially Preventable",
      provenance: {
        transcriptIds: trxByTopic("root-cause"),
        collabIds: [],
        workbookIds: workbookMM ? [workbookMM.mAndMId] : [],
      },
    },
    chronology: {
      text:
        workbookMM?.chronology ||
        "Pre-op clinic — intraoperative decrement — pause-and-remap — intentional residual — post-op trajectory documented.",
      provenance: {
        transcriptIds: trxByTopic("chronology"),
        workbookIds: workbookMM ? [workbookMM.mAndMId] : [],
        imagingIds,
        pinIds,
      },
    },
    keyDecisionPoint: {
      text:
        workbookMM?.keyDecisionPoint ||
        "Whether to continue dissection of the adherent brainstem segment after the monitoring decrement, or accept an intentional residual.",
      provenance: {
        transcriptIds: trxByTopic("root-cause"),
        collabIds: collabIdsForTopic((c) => /decision|residual|traction/i.test(c.comment || "")),
      },
    },
    rootCause: {
      text: "Traction applied to a densely adherent medial capsule before the proximal facial nerve had been definitively identified at the root exit zone, in the setting of anterior nerve displacement and no anatomical dissection plane.",
      provenance: {
        transcriptIds: trxByTopic("root-cause"),
        imagingIds,
        pinIds,
        collabIds: imagingCollabIds,
      },
    },
    contributingFactors: [
      { text: "Anterior displacement of the facial nerve making the dissection trajectory unpredictable.", provenance: { transcriptIds: trxByTopic("root-cause"), imagingIds } },
      { text: "Dense adherence at the root exit zone with no anatomical plane.", provenance: { transcriptIds: trxByTopic("root-cause"), workbookIds: workbookMM ? [workbookMM.mAndMId] : [] } },
      { text: "Reliance on a single proximal stimulation point early in the dissection.", provenance: { transcriptIds: trxByTopic("root-cause") } },
    ],
    protectiveFactors: [
      { text: "Continuous facial EMG with a low baseline stimulation threshold caught the amplitude drop early.", provenance: { transcriptIds: trxByTopic("chronology") } },
      { text: "Immediate cessation of dissection and release of all traction after the decrement, followed by systematic remapping.", provenance: { transcriptIds: trxByTopic("practice-change"), workbookIds: workbookMM ? [workbookMM.mAndMId] : [] } },
      { text: "Decision to accept an intentional 0.4 cm residual rather than pursue gross total resection.", provenance: { transcriptIds: trxByTopic("teaching"), workbookIds: workbookMM ? [workbookMM.mAndMId] : [] } },
    ],
    teachingPoints: [
      { text: "Identify the proximal facial nerve at the root exit zone before applying traction to an adherent capsule.", provenance: { transcriptIds: trxByTopic("root-cause"), reflectionIds: reflIds.slice(0, 2), pearlIds: pearlIds.slice(0, 1) } },
      { text: "Release all traction and remap before resuming after any monitoring change of 50 percent amplitude or more.", provenance: { transcriptIds: trxByTopic("practice-change"), pearlIds: pearlIds.slice(1, 2) } },
      { text: "An intentional residual over a densely adherent segment is an acceptable and preferable outcome to functional facial nerve injury.", provenance: { transcriptIds: trxByTopic("teaching"), workbookIds: workbookMM ? [workbookMM.mAndMId] : [] } },
      { text: "An anatomically intact facial nerve presenting HB IV on POD 1 has a favourable recovery trajectory (HB II at three weeks, HB I at six months in this case).", provenance: { transcriptIds: trxByTopic("chronology"), literatureIds: litIds.slice(0, 2) } },
    ],
    changeInPractice: {
      text: "Proximal facial nerve identification is now an explicit documented step in the operative record before any capsule traction on Koos III adherent tumors. A departmental pause-and-remap protocol is triggered by any facial EMG amplitude decrement of 50 percent or greater.",
      provenance: {
        transcriptIds: trxByTopic("practice-change"),
        pearlIds,
        literatureIds: litIds.slice(0, 3),
        workbookIds: workbookMM ? [workbookMM.mAndMId] : [],
      },
    },
    actionItems: [
      { text: "Adopt the departmental pause-and-remap protocol for facial nerve monitoring decrements ≥ 50 percent.", provenance: { transcriptIds: trxByTopic("action") } },
      { text: "Document proximal facial nerve identification as an explicit step in the operative record before capsule traction.", provenance: { transcriptIds: trxByTopic("action"), workbookIds: workbookMM ? [workbookMM.mAndMId] : [] } },
      { text: "Add this case to the vestibular schwannoma pre-case review library.", provenance: { transcriptIds: trxByTopic("action") } },
      { text: "Circulate the annotated preoperative and postoperative imaging to the residency.", provenance: { transcriptIds: trxByTopic("action"), imagingIds } },
    ],
    unresolvedQuestions: [
      { text: "Does a 0.4 cm brainstem-adjacent intentional residual warrant earlier surveillance imaging than the current 3-month cadence?", provenance: { transcriptIds: trxByTopic("unresolved") } },
    ],
    similarCases: [caseId], // future demo — enrich with sibling cases if present
    literatureIds: litIds,
    reflectionIds: reflIds,
    pearlIds,
    imagingIds,
    pinIds,
    supportingEvidence: {
      caseIds: [caseId],
      encounterIds: [], // populated by consumers if needed
      timelineEventIds: [], // ditto
      reflectionIds: reflIds,
      operativePearlIds: pearlIds,
      imagingIds,
      pinIds,
      collaborationIds: collaborationRecords.map((c) => c.collaborationId).concat(imagingCollabIds),
      transcriptSegmentIds: transcriptSegments.map((t) => t.trxId),
      literatureIds: litIds,
      priorMMIds: workbookMM ? [workbookMM.mAndMId] : [],
    },
  };
}

/* ---- Emerging themes helper -------------------------------------- */
function computeEmergingThemes(transcriptSegments, collaborationRecords) {
  const buckets = { chronology: 0, "root-cause": 0, "practice-change": 0, teaching: 0, literature: 0, action: 0, unresolved: 0 };
  transcriptSegments.forEach((s) => { if (buckets[s.source] != null) buckets[s.source]++; });
  const themes = Object.entries(buckets)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => ({
      key: k,
      label: {
        chronology: "Event chronology",
        "root-cause": "Root cause · traction before proximal ID",
        "practice-change": "Pause-and-remap protocol",
        teaching: "Intentional residual over adherence",
        literature: "Literature-consistent recovery trajectory",
        action: "Departmental protocol updates",
        unresolved: "Surveillance cadence for residuals",
      }[k],
      count: n,
    }));
  return themes;
}

/* ---- provider ---------------------------------------------------- */
export function MMBuilderStoreProvider({ children }) {
  const [agenda, setAgenda] = useState([]); // [{agendaId, mmId, caseId, prepared, order}]
  const [activeMMId, setActiveMMId] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [mmId]: { mmId, caseId, status, presenterPersonId, ... } }
  const [collabRecords, setCollabRecords] = useState([]);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [recording, setRecording] = useState({ state: "idle", startedAt: null, elapsedSec: 0, tickAt: null, scriptIdx: 0 });
  const [synthesisByMM, setSynthesisByMM] = useState({}); // { [mmId]: synthesisObj }
  const [publishedMMs, setPublishedMMs] = useState([]);
  const [confirmations, setConfirmations] = useState({}); // maps ni2 confirmation IDs → status

  const activeMM = activeMMId ? drafts[activeMMId] : null;
  const activeCaseId = activeMM?.caseId || null;

  /* ---- agenda -------------------------------------------------- */
  const createMMCandidate = useCallback((caseId, { tagSenior = false } = {}) => {
    const c = getCaseById(caseId);
    if (!c) return null;
    // Idempotency: reuse the existing draft for this case if one exists
    const existing = Object.values(drafts).find((d) => d.caseId === caseId && d.status !== "published");
    if (existing) return existing;
    const id = mmId();
    const workbookPrior = (getMMByCaseId(caseId) || [])[0] || null;
    const draft = {
      mmId: id,
      caseId,
      status: "preparation", // preparation | live | synthesis-ready | review | published
      createdAt: nowIso(),
      presenterPersonId: workbookPrior?.presenterPersonId || c.primaryResidentId || "RES-001",
      seniorResidentTagged: tagSenior,
      classification: {
        outcomeCategory: "Morbidity",
        severity: "Major",
        preventability: "Potentially Preventable",
      },
      priorMMId: workbookPrior?.mAndMId || null,
    };
    setDrafts((prev) => ({ ...prev, [id]: draft }));
    return draft;
  }, [drafts]);

  const addCaseToMMSession = useCallback((caseId, { tagSenior = false } = {}) => {
    // Prevent duplicate agenda entries (idempotency)
    const dup = agenda.find((a) => a.caseId === caseId && drafts[a.mmId]?.status !== "published");
    if (dup) {
      setActiveMMId(dup.mmId);
      return dup;
    }
    const draft = createMMCandidate(caseId, { tagSenior });
    if (!draft) return null;
    const item = {
      agendaId: agendaId(),
      mmId: draft.mmId,
      caseId,
      addedAt: nowIso(),
      prepared: false,
      order: agenda.length + 1,
    };
    setAgenda((prev) => [...prev, item]);
    setActiveMMId(draft.mmId);
    return item;
  }, [agenda, drafts, createMMCandidate]);

  const removeCaseFromMMSession = useCallback((agendaIdArg) => {
    setAgenda((prev) => prev.filter((a) => a.agendaId !== agendaIdArg));
  }, []);

  const reorderMMAgenda = useCallback((orderedAgendaIds) => {
    setAgenda((prev) => {
      const byId = Object.fromEntries(prev.map((a) => [a.agendaId, a]));
      return orderedAgendaIds
        .map((id, i) => (byId[id] ? { ...byId[id], order: i + 1 } : null))
        .filter(Boolean);
    });
  }, []);

  const markPrepared = useCallback((agendaIdArg, prepared) => {
    setAgenda((prev) => prev.map((a) => (a.agendaId === agendaIdArg ? { ...a, prepared } : a)));
  }, []);

  /* ---- collaboration records (pre-conference discussion) ------- */
  const appendMMCollaborationRecord = useCallback((input) => {
    const rec = {
      collaborationId: collabId(),
      contextType: "M&M",
      contextId: input.mmId || activeMMId,
      caseId: input.caseId || activeCaseId,
      threadId: input.threadId || `MM-THREAD-${input.mmId || activeMMId}`,
      authorPersonId: input.authorPersonId || "ATT-001",
      mentionedPersonIds: input.mentionedPersonIds || [],
      dateAndTime: nowIso(),
      comment: input.comment,
      section: input.section || null, // for correction records: target synthesis section
      previousText: input.previousText || null,
      revisedText: input.revisedText || null,
      resolved: "No",
      resolutionSummary: "",
      kind: input.kind || "discussion", // "discussion" | "correction" | "confirmation"
      demo: true,
    };
    setCollabRecords((prev) => [...prev, rec]);
    return rec;
  }, [activeMMId, activeCaseId]);

  /* ---- recording & transcript --------------------------------- */
  const startRecording = useCallback(() => {
    if (!activeMMId) return;
    // Idempotency: if already live, no-op
    setRecording((r) => (r.state === "live" ? r : { ...r, state: "live", startedAt: nowIso(), elapsedSec: 0, tickAt: Date.now(), scriptIdx: 0 }));
    setDrafts((prev) => ({ ...prev, [activeMMId]: { ...prev[activeMMId], status: "live" } }));
  }, [activeMMId]);
  const pauseRecording = useCallback(() => {
    setRecording((r) => (r.state !== "live" ? r : { ...r, state: "paused" }));
  }, []);
  const resumeRecording = useCallback(() => {
    setRecording((r) => (r.state !== "paused" ? r : { ...r, state: "live", tickAt: Date.now() }));
  }, []);
  const endConference = useCallback(() => {
    setRecording((r) => ({ ...r, state: "ended" }));
    if (activeMMId) {
      setDrafts((prev) => ({ ...prev, [activeMMId]: { ...prev[activeMMId], status: "synthesis-ready" } }));
    }
  }, [activeMMId]);

  const appendTranscriptSegment = useCallback((input) => {
    const rec = {
      trxId: trxId(),
      mmId: input.mmId || activeMMId,
      caseId: input.caseId || activeCaseId,
      speakerPersonId: input.speakerPersonId,
      role: input.role,
      text: input.text,
      offsetSec: input.offsetSec,
      source: input.source, // topic bucket used by the synthesis generator
      timestamp: nowIso(),
      demo: true,
    };
    setTranscriptSegments((prev) => [...prev, rec]);
    return rec;
  }, [activeMMId, activeCaseId]);

  /* ---- transcript scheduler ----------------------------------- */
  const tickRef = useRef();
  useEffect(() => {
    if (recording.state !== "live") return;
    tickRef.current = setInterval(() => {
      setRecording((r) => {
        if (r.state !== "live") return r;
        const now = Date.now();
        const delta = r.tickAt ? Math.floor((now - r.tickAt) / 1000) : 0;
        const newElapsed = r.elapsedSec + delta;
        // Compression factor: 1s real ≈ 4s scripted so demo lands quickly
        const scriptTime = newElapsed * 4;
        let scriptIdx = r.scriptIdx;
        while (
          scriptIdx < VS_RAO_001_TRANSCRIPT_SCRIPT.length &&
          VS_RAO_001_TRANSCRIPT_SCRIPT[scriptIdx].offsetSec <= scriptTime
        ) {
          const seg = VS_RAO_001_TRANSCRIPT_SCRIPT[scriptIdx];
          appendTranscriptSegment({
            speakerPersonId: seg.speaker,
            role: seg.role,
            text: seg.text,
            offsetSec: seg.offsetSec,
            source: seg.source,
          });
          scriptIdx++;
        }
        return { ...r, elapsedSec: newElapsed, tickAt: now, scriptIdx };
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [recording.state, appendTranscriptSegment]);

  /* ---- synthesis --------------------------------------------- */
  const generateMMSynthesis = useCallback(
    async ({ mmId: id = activeMMId, demoPins = [], demoImagingComments = [], live = false } = {}) => {
      if (!id) return null;
      const draft = drafts[id];
      if (!draft) return null;

      const transcriptForMM = transcriptSegments.filter((s) => s.mmId === id);
      const collabForMM = collabRecords.filter((c) => c.contextId === id);

      let synth;
      if (live) {
        try {
          const resp = await synthesizeMMLive({
            mmId: id,
            caseId: draft.caseId,
            transcriptSegments: transcriptForMM,
            collaborationRecords: collabForMM,
            demoPins,
            demoImagingComments,
          });
          synth = resp?.synthesis;
        } catch (e) {
          // Silent fallback to deterministic path so the demo never
          // hard-breaks. The UI can surface the error separately.
          console.warn("Live MM synthesis failed, falling back to deterministic:", e);
          synth = null;
        }
      }
      if (!synth) {
        synth = generateDeterministicSynthesis({
          caseId: draft.caseId,
          transcriptSegments: transcriptForMM,
          collaborationRecords: collabForMM,
          demoPins,
          demoImagingComments,
        });
      }

      const prior = synthesisByMM[id];
      const reviews = prior?.reviews || [];
      const finalSynth = { ...synth, reviews };
      setSynthesisByMM((prev) => ({ ...prev, [id]: finalSynth }));
      setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], status: "review" } }));
      return finalSynth;
    },
    [activeMMId, drafts, transcriptSegments, collabRecords, synthesisByMM]
  );

  const reviewMMSynthesisSection = useCallback(
    ({ mmId: id = activeMMId, sectionKey, action, correctionText, authorPersonId = "ATT-001" }) => {
      if (!id) return null;
      const synth = synthesisByMM[id];
      if (!synth) return null;
      const prior = synth[sectionKey];
      const priorText = typeof prior?.text === "string" ? prior.text : Array.isArray(prior) ? prior.map((x) => x.text).join(" | ") : "";
      const review = {
        reviewId: `REVIEW-${id}-${sectionKey}-${Date.now()}`,
        sectionKey,
        action, // approve | flag | exclude | regenerate | correct
        priorText,
        correctionText: correctionText || null,
        authorPersonId,
        timestamp: nowIso(),
      };
      // Attributed collaboration record for every edit
      const collab = appendMMCollaborationRecord({
        mmId: id,
        authorPersonId,
        comment: `[${sectionKey}] ${action}${correctionText ? ` · ${correctionText}` : ""}`,
        kind: "correction",
        section: sectionKey,
        previousText: priorText,
        revisedText: correctionText || null,
      });
      review.collaborationId = collab.collaborationId;

      setSynthesisByMM((prev) => {
        const s = prev[id] || {};
        const nextReviews = [...(s.reviews || []), review];
        // For "correct" actions, we replace .text but keep priorText inside reviews
        const updated = { ...s, reviews: nextReviews };
        if (action === "correct" && correctionText) {
          if (Array.isArray(s[sectionKey])) {
            // Not typical; skip inline mutation for arrays
          } else if (typeof s[sectionKey] === "object" && s[sectionKey] !== null) {
            updated[sectionKey] = { ...s[sectionKey], text: correctionText, editedByReview: true };
          }
        }
        return { ...prev, [id]: updated };
      });
      return review;
    },
    [activeMMId, synthesisByMM, appendMMCollaborationRecord]
  );

  const sectionApproved = useCallback((mmIdArg, sectionKey) => {
    const s = synthesisByMM[mmIdArg];
    if (!s) return false;
    const rs = (s.reviews || []).filter((r) => r.sectionKey === sectionKey);
    if (!rs.length) return false;
    const last = rs[rs.length - 1];
    return last.action === "approve" || last.action === "correct";
  }, [synthesisByMM]);

  const sectionExcluded = useCallback((mmIdArg, sectionKey) => {
    const s = synthesisByMM[mmIdArg];
    if (!s) return false;
    const rs = (s.reviews || []).filter((r) => r.sectionKey === sectionKey);
    if (!rs.length) return false;
    return rs[rs.length - 1].action === "exclude";
  }, [synthesisByMM]);

  /* ---- publish ------------------------------------------------ */
  const publishMMToDepartmentMemory = useCallback((mmIdArg = activeMMId) => {
    if (!mmIdArg) return null;
    const draft = drafts[mmIdArg];
    const synth = synthesisByMM[mmIdArg];
    if (!draft || !synth) return null;
    // Idempotency: if already published, return existing record
    const existing = publishedMMs.find((p) => p.mmId === mmIdArg);
    if (existing) return existing;
    const finalSynth = filterExcluded(synth, mmIdArg, sectionExcluded);
    const publishedRecord = {
      publishedId: publishedId(),
      mmId: mmIdArg,
      caseId: draft.caseId,
      publishedAt: nowIso(),
      status: "Published",
      classification: synth.classification,
      synthesis: finalSynth,
      transcriptIds: transcriptSegments.filter((s) => s.mmId === mmIdArg).map((s) => s.trxId),
      collaborationIds: collabRecords.filter((c) => c.contextId === mmIdArg).map((c) => c.collaborationId),
      timelineEventId: timelineEventId(),
      demo: true,
    };
    setPublishedMMs((prev) => [...prev, publishedRecord]);
    setDrafts((prev) => ({ ...prev, [mmIdArg]: { ...prev[mmIdArg], status: "published" } }));
    return publishedRecord;
  }, [activeMMId, drafts, synthesisByMM, publishedMMs, transcriptSegments, collabRecords, sectionExcluded]);

  /* ---- confirmations (Ni² intent cards) ----------------------- */
  const markConfirmation = useCallback((confId, status) => {
    setConfirmations((prev) => ({ ...prev, [confId]: status }));
  }, []);

  /* ---- reads for department memory (unified read lives in
        repository.searchDepartmentMemory — this exposes the
        published slice so the repository can merge it in). ---- */
  const getPublishedMMs = useCallback(() => publishedMMs, [publishedMMs]);
  const getPreviousMMs = useCallback(() => {
    // workbook M&Ms + demo published
    // (unified via repository for external callers; this is convenience.)
    return publishedMMs;
  }, [publishedMMs]);

  const value = useMemo(
    () => ({
      // state
      agenda,
      activeMMId,
      setActiveMMId,
      drafts,
      activeMM,
      activeCaseId,
      collabRecords,
      transcriptSegments,
      recording,
      synthesisByMM,
      publishedMMs,
      confirmations,
      // reads
      getEmergingThemes: (id = activeMMId) =>
        computeEmergingThemes(
          transcriptSegments.filter((s) => s.mmId === id),
          collabRecords.filter((c) => c.contextId === id)
        ),
      sectionApproved,
      sectionExcluded,
      getPublishedMMs,
      getPreviousMMs,
      // writes
      createMMCandidate,
      addCaseToMMSession,
      removeCaseFromMMSession,
      reorderMMAgenda,
      markPrepared,
      appendMMCollaborationRecord,
      startRecording,
      pauseRecording,
      resumeRecording,
      endConference,
      appendTranscriptSegment,
      generateMMSynthesis,
      reviewMMSynthesisSection,
      publishMMToDepartmentMemory,
      markConfirmation,
    }),
    [
      agenda, activeMMId, drafts, activeMM, activeCaseId, collabRecords, transcriptSegments,
      recording, synthesisByMM, publishedMMs, confirmations, sectionApproved, sectionExcluded,
      getPublishedMMs, getPreviousMMs,
      createMMCandidate, addCaseToMMSession, removeCaseFromMMSession, reorderMMAgenda,
      markPrepared, appendMMCollaborationRecord, startRecording, pauseRecording, resumeRecording,
      endConference, appendTranscriptSegment, generateMMSynthesis, reviewMMSynthesisSection,
      publishMMToDepartmentMemory, markConfirmation,
    ]
  );

  return <MMBuilderContext.Provider value={value}>{children}</MMBuilderContext.Provider>;
}

function filterExcluded(synth, mmIdArg, sectionExcluded) {
  // Strip out excluded sections from a copy so publication reflects
  // the final approved synthesis (not the initial draft).
  const out = { ...synth };
  ["teachingPoints", "actionItems", "contributingFactors", "protectiveFactors", "unresolvedQuestions"].forEach((k) => {
    if (Array.isArray(out[k])) {
      out[k] = out[k].filter((_, i) => !sectionExcluded(mmIdArg, `${k}[${i}]`));
    }
  });
  ["chronology", "rootCause", "keyDecisionPoint", "changeInPractice", "classification"].forEach((k) => {
    if (sectionExcluded(mmIdArg, k)) out[k] = null;
  });
  return out;
}

export function useMMBuilderStore() {
  const ctx = useContext(MMBuilderContext);
  if (!ctx) throw new Error("useMMBuilderStore must be used inside <MMBuilderStoreProvider>");
  return ctx;
}

// Provide a passive fallback so the intent router can be used from
// outside the provider (e.g. Dashboard Ni²) without crashing.
export function useOptionalMMBuilderStore() {
  return useContext(MMBuilderContext);
}
